import { AddBox, Delete, Edit } from "@mui/icons-material";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, FormLabel, InputLabel, MenuItem, Modal, Paper, Select, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import axiosInstance from "../../../utils/axiosInstance";

const Payments = () => {
    const [payments, setPayments] = useState([]);
    const [participants, setParticipants] = useState([]);
    const [plans, setPlans] = useState([]);
    const [managers, setManagers] = useState([]);
    const [requests, setRequests] = useState([]);

    const [payment, setPayment] = useState({
        amount: '',
        payment_date: '',
        status: '',
        reference_id: '',
        client_id: '',
        user_id: '',
    });

    const [selectedPayment, setSelectedPayment] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    const getPaymentsRef = useRef();

    // Filter payments based on search query
    const filteredPayments = payments.filter((payment) => {
        const client = participants.find(p =>
            p.participant_id === payment.client_id ||
            p.guest_id === payment.client_id ||
            p.member_id === payment.client_id
        );

        const manager = managers.find(m => m.user_id === payment.user_id);

        return (
            (manager?.name?.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (client?.name?.toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
    });

    const paginatedPayments = filteredPayments.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Delete confirmation handlers
    const handleOpenDeleteModal = (payment) => {
        setIsDeleteModalOpen(true);
        setSelectedPayment(payment);
    };

    const handleOpenEditModal = (payment) => {
        setIsEditModalOpen(true);
        setSelectedPayment(payment);
    };

    const handleAddPayment = async () => {
        setOpen(true);

        try {
            await axiosInstance.post(`payments/create`, payment, {
                headers: { 'Content-Type': 'application/json' }
            });
            setMessage(`Success, successfully added payment`);
            setTimeout(() => {
                setOpen(false);
                if (getPaymentsRef.current) getPaymentsRef.current();
                setIsAddModalOpen(false);
            }, 2000);
        } catch (error) {
            setMessage(`Failure, ${error.response?.data?.error || error.message}`);
            setTimeout(() => { setOpen(false) }, 2000);
        }
    };

    const handleEditPayment = async () => {
        setOpen(true);
        const payload = {
            amount: selectedPayment.amount,
            payment_date: selectedPayment?.payment_date?.slice(0, 10),
            status: selectedPayment.status,
            reference_id: selectedPayment.reference_id,
            client_id: selectedPayment.client_id,
            user_id: selectedPayment.user_id,
        };

        try {
            await axiosInstance.put(`payments/update/${selectedPayment.payment_id}`, payload);
            setMessage(`Success, successfully edited payment`);
            setTimeout(() => {
                setOpen(false);
                setSelectedPayment(null);
                if (getPaymentsRef.current) getPaymentsRef.current();
                setIsEditModalOpen(false);
            }, 2000);
        } catch (error) {
            setMessage(`Failure, ${error.response?.data?.error || error.message}`);
            setTimeout(() => { setOpen(false) }, 2000);
        }
    };

    const handleDeletePayment = async () => {
        setOpen(true);

        try {
            await axiosInstance.delete(`payments/delete/${selectedPayment.payment_id}`);
            setMessage(`Success, successfully deleted payment`);
            setTimeout(() => {
                setOpen(false);
                setSelectedPayment(null);
                if (getPaymentsRef.current) getPaymentsRef.current();
                setIsDeleteModalOpen(false);
            }, 2000);
        } catch (error) {
            setMessage(`Failure, ${error.response?.data?.error || error.message}`);
            setTimeout(() => { setOpen(false) }, 2000);
        }
    };

    const getPlanName = (id) => {
        return plans.find((plan) => plan.plan_id === id)?.title || '';
    };

    const getMatchingUserPlan = (user_id, plan_id) => {
        const planName = getPlanName(plan_id);
        const matchingUser = participants.find((participant) =>
            participant.guest_id === user_id || participant.member_id === user_id
        );

        if (matchingUser) {
            return `${matchingUser.name} - ${planName}`;
        }
        return '';
    };

    // Data fetching functions
    const getParticipants = async () => {
        try {
            const response = await axiosInstance.get(`auth/all_users`);
            setParticipants(response.data.users || []);
        } catch (error) {
            setParticipants([]);
        }
    };

    const getPayments = async () => {
        try {
            const result = await axiosInstance.get(`payments/all`);
            setPayments(result.data.data || []);
        } catch (error) {
            setPayments([]);
        }
    };

    const getPlans = async () => {
        try {
            const response = await axiosInstance.get(`plans/all`);
            setPlans(response.data.data || []);
        } catch (error) {
            setPlans([]);
        }
    };

    const getManagers = async () => {
        try {
            const response = await axiosInstance.get(`auth/users`);
            setManagers(response.data.users || []);
        } catch (error) {
            setManagers([]);
        }
    };

    const getRequests = async () => {
        try {
            const response = await axiosInstance.get(`membershipsubscription/all`);
            setRequests(response.data.data || []);
        } catch (error) {
            setRequests([]);
        }
    };

    // Initialize data fetching
    useEffect(() => {
        const loadAllData = async () => {
            setIsLoading(true);
            try {
                await Promise.all([
                    getManagers(),
                    getPlans(),
                    getRequests(),
                    getPayments(),
                    getParticipants()
                ]);
            } finally {
                setIsLoading(false);
            }
        };

        getPaymentsRef.current = getPayments;
        loadAllData();
    }, []);

    if (isLoading) {
        return <div className="flex justify-center items-center">
            <CircularProgress className="text-blue-800" />
        </div>;
    }

    return (
        <div>
            <div className="md:flex justify-between md:flex-wrap">
                <Typography variant='h4' className="text-blue-800">Manage payments</Typography>
                <div className="mt-4 md:mt-0">
                    <Button className="flex gap-3" variant="outlined" onClick={() => setIsAddModalOpen(true)}>
                        <AddBox /> Add payment
                    </Button>
                </div>
            </div>
            <br />

            <div className="p-4">
                <TextField
                    label="Search for payments by client or manager"
                    variant="outlined"
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />

                <TableContainer component={Paper} className="shadow mt-4" sx={{ color: "#1f2937" }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Client</TableCell>
                                <TableCell>Manager</TableCell>
                                <TableCell>Plan</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Payment Date</TableCell>
                                <TableCell>Created At</TableCell>
                                <TableCell>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedPayments.map((payment, index) => {
                                const client = participants.find(p =>
                                    p.participant_id === payment.client_id ||
                                    p.guest_id === payment.client_id ||
                                    p.member_id === payment.client_id
                                );

                                const manager = managers.find(m => m.user_id === payment.user_id);
                                const request = requests.find(r => r.subscription_id === payment.reference_id);
                                const plan = plans.find(p => p.plan_id === request?.plan_id);

                                return (
                                    <TableRow key={payment.payment_id}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{client?.name || payment.client_id}</TableCell>
                                        <TableCell>{manager?.name || payment.user_id}</TableCell>
                                        <TableCell>{plan?.title || "No plan"}</TableCell>  {/* Display plan name */}
                                        <TableCell>{Number(payment.amount).toLocaleString()}</TableCell>
                                        <TableCell>{payment.status || "inactive"}</TableCell>
                                        <TableCell>{payment.payment_date?.slice(0, 10)}</TableCell>
                                        <TableCell>{payment.createdAt?.slice(0, 10)}</TableCell>
                                        <TableCell className="flex gap-2">
                                            <Edit
                                                color="primary"
                                                onClick={() => handleOpenEditModal(payment)}
                                                className="cursor-pointer"
                                            />
                                            <Delete
                                                color="error"
                                                onClick={() => handleOpenDeleteModal(payment)}
                                                className="cursor-pointer"
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={filteredPayments.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </TableContainer>
            </div>

            {/* Delete Confirmation Modal */}
            <Dialog open={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
                <DialogTitle className="text-blue-950">Delete Confirmation</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this payment?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDeleteModalOpen(false)} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleDeletePayment} color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Payment Modal */}
            <Modal
                open={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                className="flex justify-center items-center"
            >
                <div className='bg-white w-[90%] lg:w-[40%] shadow p-8 rounded-sm'>
                    <h2 className="text-blue-950 mb-8">Edit payment</h2>
                    {selectedPayment && (
                        <div>
                            <TextField
                                variant="outlined"
                                label="Enter Amount"
                                fullWidth
                                type="number"
                                value={selectedPayment.amount}
                                onChange={(e) => setSelectedPayment(prev => ({ ...prev, amount: e.target.value }))}
                                style={{ marginBottom: 10 }}
                            />

                            <FormControl fullWidth style={{ marginBottom: 15 }}>
                                <InputLabel>Subscription plan</InputLabel>
                                <Select
                                    label="Select subscription plan"
                                    value={selectedPayment.reference_id}
                                    onChange={(e) => setSelectedPayment(prev => ({ ...prev, reference_id: e.target.value }))}
                                >
                                    {requests.map((request) => (
                                        <MenuItem key={request.subscription_id} value={request.subscription_id}>
                                            {getMatchingUserPlan(request.membership_id, request.plan_id)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormLabel>Payment date</FormLabel>
                            <TextField
                                variant="standard"
                                fullWidth
                                type="date"
                                value={selectedPayment?.payment_date?.slice(0, 10) || ''}
                                onChange={(e) => setSelectedPayment(prev => ({ ...prev, payment_date: e.target.value }))}
                                style={{ marginBottom: 10 }}
                            />

                            <div style={{ margin: '6px 0px' }}>
                                <input
                                    type="checkbox"
                                    checked={selectedPayment.status === "active"}
                                    onChange={(e) => setSelectedPayment(prev => ({
                                        ...prev,
                                        status: e.target.checked ? "active" : "inactive"
                                    }))}
                                    style={{ marginRight: 10 }}
                                />
                                <FormLabel>Active payment</FormLabel>
                            </div>

                            <FormControl fullWidth style={{ marginBottom: 15 }}>
                                <InputLabel>Client</InputLabel>
                                <Select
                                    label="Select client"
                                    value={selectedPayment.client_id}
                                    onChange={(e) => setSelectedPayment(prev => ({ ...prev, client_id: e.target.value }))}
                                >
                                    {participants.map((participant) => (
                                        <MenuItem
                                            key={participant.participant_id || participant.guest_id || participant.member_id}
                                            value={participant.participant_id || participant.guest_id || participant.member_id}
                                        >
                                            {participant.name} - <span className="text-blue-700 ml-2">{participant.role?.toUpperCase()}</span>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth style={{ marginBottom: 15 }}>
                                <InputLabel>Manager</InputLabel>
                                <Select
                                    label="Select manager"
                                    value={selectedPayment.user_id}
                                    onChange={(e) => setSelectedPayment(prev => ({ ...prev, user_id: e.target.value }))}
                                >
                                    {managers.map((manager) => (
                                        <MenuItem key={manager.user_id} value={manager.user_id}>
                                            {manager.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                    )}

                    <div className="flex items-center justify-center gap-8 w-[90%] mx-auto mt-4">
                        <Button
                            onClick={() => setIsEditModalOpen(false)}
                            color="secondary"
                            fullWidth
                            variant="outlined"
                        >
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            fullWidth
                            variant="outlined"
                            onClick={handleEditPayment}
                        >
                            Save
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Add Payment Modal */}
            <Modal
                open={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                className="flex justify-center items-center"
            >
                <div className='bg-white w-[90%] lg:w-[40%] shadow p-8 rounded-sm'>
                    <h2 className="text-blue-950 mb-6">Add payment</h2>
                    <div>
                        <TextField
                            variant="outlined"
                            label="Enter Amount"
                            fullWidth
                            type="number"
                            value={payment.amount}
                            onChange={(e) => setPayment(prev => ({ ...prev, amount: e.target.value }))}
                            style={{ marginBottom: 10 }}
                        />

                        <FormControl fullWidth style={{ marginBottom: 15 }}>
                            <InputLabel>Subscription request</InputLabel>
                            <Select
                                label="Select subscription request"
                                value={payment.reference_id}
                                onChange={(e) => setPayment(prev => ({ ...prev, reference_id: e.target.value }))}
                            >
                                {requests.map((request) => (
                                    <MenuItem key={request.subscription_id} value={request.subscription_id}>
                                        {getMatchingUserPlan(request.membership_id, request.plan_id)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormLabel>Payment date</FormLabel>
                        <TextField
                            variant="standard"
                            fullWidth
                            type="date"
                            value={payment.payment_date}
                            onChange={(e) => setPayment(prev => ({ ...prev, payment_date: e.target.value }))}
                            style={{ marginBottom: 10 }}
                        />

                        <div style={{ margin: '6px 0px' }}>
                            <input
                                type="checkbox"
                                checked={payment.status === "active"}
                                onChange={(e) => setPayment(prev => ({
                                    ...prev,
                                    status: e.target.checked ? "active" : "inactive"
                                }))}
                                style={{ marginRight: 10 }}
                            />
                            <FormLabel>Active payment</FormLabel>
                        </div>

                        <FormControl fullWidth style={{ marginBottom: 15 }}>
                            <InputLabel>Client</InputLabel>
                            <Select
                                label="Select client"
                                value={payment.client_id}
                                onChange={(e) => setPayment(prev => ({ ...prev, client_id: e.target.value }))}
                            >
                                {participants.map((participant) => (
                                    <MenuItem
                                        key={participant.participant_id || participant.guest_id || participant.member_id}
                                        value={participant.participant_id || participant.guest_id || participant.member_id}
                                    >
                                        {participant.name} - <span className="text-blue-700 ml-2">{participant.role?.toUpperCase()}</span>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth style={{ marginBottom: 15 }}>
                            <InputLabel>Manager</InputLabel>
                            <Select
                                label="Select manager"
                                value={payment.user_id}
                                onChange={(e) => setPayment(prev => ({ ...prev, user_id: e.target.value }))}
                            >
                                {managers.map((manager) => (
                                    <MenuItem key={manager.user_id} value={manager.user_id}>
                                        {manager.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    <div className="flex items-center justify-center gap-8 w-[90%] mx-auto mt-4">
                        <Button
                            onClick={() => setIsAddModalOpen(false)}
                            color="secondary"
                            variant="outlined"
                            fullWidth
                        >
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            variant="outlined"
                            fullWidth
                            onClick={handleAddPayment}
                        >
                            Save
                        </Button>
                    </div>
                </div>
            </Modal>

            <Snackbar
                open={open}
                autoHideDuration={6000}
                onClose={() => setOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    severity={
                        message.startsWith("Success") ? 'success' :
                            message.startsWith('Failure') ? 'error' :
                                'info'
                    }
                >
                    {message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default Payments;